import {CommonModule, DatePipe} from '@angular/common';
import {Component, computed, inject, signal} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {
  ScopedTokenAdminResponse,
  SelecthorAdminScopedTokenControllerService,
  TokenScope,
} from '@frontend/shared-api';
import {FormsModule} from '@angular/forms';
import {TableModule} from 'primeng/table';
import {Button} from 'primeng/button';
import {Select} from 'primeng/select';
import {TooltipModule} from 'primeng/tooltip';
import {TranslocoPipe} from '@jsverse/transloco';
import {BackgroundCardComponent} from '../../../shared/components/titled-background-card/background-card.component';
import {LoadingComponent} from '../../../shared/components/loading/loading.component';
import {NoDataComponent} from '../../../shared/components/no-data/no-data.component';
import {ToastService} from '@frontend/toast';
import {ConfirmationDialogService} from '../../../shared/components/dialog/confirmation/confirmation-dialog.service';

interface SelectOptionVM<T> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-selecthor-admin-tokens',
  templateUrl: './selecthor-admin-tokens.component.html',
  imports: [
    CommonModule,
    DatePipe,
    FormsModule,
    TableModule,
    Button,
    Select,
    TooltipModule,
    TranslocoPipe,
    BackgroundCardComponent,
    LoadingComponent,
    NoDataComponent,
  ],
})
export class SelecthorAdminTokensComponent {
  private readonly scopedTokenService = inject(SelecthorAdminScopedTokenControllerService);
  private readonly toastService = inject(ToastService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);

  readonly loading = signal(false);
  readonly resendingTokenId = signal<number | null>(null);
  readonly scopedTokens = signal<ScopedTokenAdminResponse[]>([]);

  readonly selectedScope = signal<TokenScope | null>(null);
  readonly selectedUsed = signal<boolean | null>(null);

  readonly scopeOptions: SelectOptionVM<TokenScope | null>[] = [
    {label: 'All scopes', value: null},
    ...Object.values(TokenScope).map((scope) => ({label: scope, value: scope})),
  ];

  readonly usedOptions: SelectOptionVM<boolean | null>[] = [
    {label: 'All', value: null},
    {label: 'Used', value: true},
    {label: 'Unused', value: false},
  ];

  readonly hasTokens = computed(() => this.scopedTokens().length > 0);

  constructor() {
    this.loadScopedTokens();
  }

  async loadScopedTokens(): Promise<void> {
    this.loading.set(true);

    try {
      const scopedTokens = await firstValueFrom(
        this.scopedTokenService.getScopedTokens({
          scope: this.selectedScope() ?? undefined,
          used: this.selectedUsed() ?? undefined,
        }),
      );
      this.scopedTokens.set(scopedTokens ?? []);
    } finally {
      this.loading.set(false);
    }
  }

  async onScopeFilterChange(scope: TokenScope | null): Promise<void> {
    this.selectedScope.set(scope);
    await this.loadScopedTokens();
  }

  async onUsedFilterChange(used: boolean | null): Promise<void> {
    this.selectedUsed.set(used);
    await this.loadScopedTokens();
  }

  confirmResend(token: ScopedTokenAdminResponse): void {
    if (token.used) {
      return;
    }

    this.confirmDialogService.confirm({
      headerKey: 'selecthorAdmin.scopedTokens.resendInvitationEmail',
      messageKey: 'selecthorAdmin.scopedTokens.confirmResendInvitationEmail',
      accept: () => {
        void this.resend(token);
      },
    });
  }

  async resend(token: ScopedTokenAdminResponse): Promise<void> {
    if (token.used || token.id == null) {
      return;
    }

    this.resendingTokenId.set(token.id);
    try {
      const updatedToken = await firstValueFrom(
        this.scopedTokenService.resendScopedToken({tokenId: token.id}),
      );
      this.scopedTokens.set(
        this.scopedTokens().map((item) => (item.id === updatedToken.id ? updatedToken : item)),
      );
      await this.toastService.showSuccess('common.success', 'common.success');
    } catch {
      await this.toastService.showError('common.error', 'common.error');
    } finally {
      this.resendingTokenId.set(null);
    }
  }
}
